/**
 * `time_tracking` — `TimeTrackingValue` (`specs/M2-tier2-column-types.md` §5.7,
 * `[09§A.7 time_tracking]`, `[cvs§2 time_tracking]`).
 *
 * Two stores, and monday keeps them apart:
 *
 *  - the **cell value**, `{"running":"false","duration":<seconds>,"startDate":<epoch s>,
 *    "changed_at":…}` — `running` is the **string** `"false"` `[cvs§4.4]` and `startDate` is
 *    epoch **seconds** (unlike `file`'s `createdAt`, which is milliseconds);
 *  - the **session log**, which is *not* in `value` at all: `TimeTrackingValue.history[]` is
 *    `{id, status, started_at, ended_at, started_user_id, ended_user_id,
 *    manually_entered_start_date, …_start_time, …_end_date, …_end_time, created_at}`.
 *    We store it in `time_tracking_sessions` (migration 0013) because a JSON array inside the
 *    cell could not be indexed, attributed or edited per session.
 *
 * `duration` is therefore **derived**: the sum of the sessions' lengths plus, while a timer
 * runs, `now - started_at`. `toText` is `HH:MM:SS` zero-padded and **`""` when the duration is
 * 0** (measured: 18 of 25 sampled cells) — note `24:00:00`, i.e. hours are not wrapped at 24.
 *
 * *"cannot be updated or cleared via the API. The timer can only be started, stopped, and
 * managed through the monday.com UI or automations."* `[09§A.7]` — so `writable: false` and the
 * start/stop/manual-entry paths are service endpoints, not cell writes.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { pad2, textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { timeTrackingFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const timeTrackingSessionSchema = z.looseObject({
    id: z.string(),
    /** `"active"` on every sampled row `[cvs§2 time_tracking]`. */
    status: z.string().optional(),
    started_at: z.string(),
    ended_at: z.string().nullable().optional(),
    started_user_id: z.string().nullable().optional(),
    ended_user_id: z.string().nullable().optional(),
    manually_entered_start_date: z.boolean().optional(),
    manually_entered_start_time: z.boolean().optional(),
    manually_entered_end_date: z.boolean().optional(),
    manually_entered_end_time: z.boolean().optional(),
    created_at: z.string().optional(),
});
export const timeTrackingValueSchema = z.looseObject({
    /** Seconds. */
    duration: z.number().int().min(0),
    /**
     * monday ships the **string** `"false"` here `[cvs§4.4]`; we store a real boolean — and
     * **omit the key** when monday did (a reset cell can be a bare `{"duration":0}`), so a
     * re-export is byte-identical. Absent means not running.
     */
    running: z.boolean().optional(),
    /** Epoch **seconds** of the running (or last) session's start; null on a reset timer. */
    startDate: z.number().int().nullable().optional(),
    changed_at: z.string().nullable().optional(),
});
export const timeTrackingSettingsSchema = z.looseObject({
    /** The one setting in the captured dialog: *Time presentation ▸ Show seconds*. */
    showSeconds: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
function asBoolean(raw) {
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'object') {
        throw new ColumnValueError('time_tracking', `expected an object, received ${typeof raw}`);
    }
    const record = raw;
    const rawDuration = record.duration;
    const duration = rawDuration === null || rawDuration === undefined ? 0 : Number(rawDuration);
    if (!Number.isFinite(duration) || duration < 0) {
        throw new ColumnValueError('time_tracking', '"duration" must be a non-negative number');
    }
    const running = asBoolean(record.running);
    /*
     * A reset timer is stored by monday as `{"duration":0}` (and as
     * `{"running":"false","duration":0,"startDate":null}`), and its `/v2` read returns that
     * object rather than null — so the **import** keeps it, byte-for-byte, while a *write* of an
     * all-zero value means "empty" and collapses to null. The rendering is `""` either way.
     */
    if (duration === 0 && !running && !keepVolatile)
        return null;
    const value = { duration: Math.trunc(duration) };
    if (!keepVolatile || 'running' in record)
        value.running = running;
    const start = record.startDate ?? record.started_at;
    if (typeof start === 'number')
        value.startDate = Math.trunc(start);
    else if (typeof start === 'string' && start !== '') {
        const parsed = Date.parse(start);
        if (!Number.isNaN(parsed))
            value.startDate = Math.floor(parsed / 1000);
    }
    else if (keepVolatile && 'startDate' in record) {
        // monday ships `"startDate": null` on a reset timer; the key round-trips `[cvs§4.2]`.
        value.startDate = record.startDate;
    }
    // monday ships `"changed_at": null` on many cells; keeping the key (null included) is
    // what makes a re-export byte-identical `[cvs§4.2]`.
    if (keepVolatile && 'changed_at' in record)
        value.changed_at = record.changed_at;
    return value;
}
/** `"24:00:00"` — hours are **not** wrapped at 24 (measured). `""` for 0 seconds. */
export function durationText(seconds) {
    if (seconds <= 0)
        return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds % 60)}`;
}
/** Seconds one session contributes; an open session counts up to `now`. */
export function sessionSeconds(session, now = new Date()) {
    const start = Date.parse(session.started_at);
    if (Number.isNaN(start))
        return 0;
    const end = session.ended_at ? Date.parse(session.ended_at) : now.getTime();
    if (Number.isNaN(end) || end <= start)
        return 0;
    return Math.floor((end - start) / 1000);
}
export function totalSeconds(sessions, now = new Date()) {
    return sessions.reduce((sum, session) => sum + sessionSeconds(session, now), 0);
}
export const timeTrackingColumn = defineColumnType({
    type: 'time_tracking',
    title: 'Time Tracking',
    description: 'A timer with a session log.',
    apiFragmentName: 'TimeTrackingValue',
    legacyTypeName: 'duration',
    idPrefix: 'time_tracking',
    valueSchema: timeTrackingValueSchema,
    settingsSchema: timeTrackingSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: timeTrackingSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: timeTrackingFilterConditions,
    filterOperators: operatorsOf(timeTrackingFilterConditions),
    aggregations: ['sum', 'avg', 'min', 'max', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('time_tracking', 'the timer is started and stopped, not written');
    },
    toText(value) {
        return value === null ? '' : durationText(value.duration);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(durationText(value.duration)), number: value.duration, date: null };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return (a.duration === b.duration &&
            (a.running ?? false) === (b.running ?? false) &&
            (a.startDate ?? null) === (b.startDate ?? null));
    },
    toActivityValue(value) {
        return value === null ? null : { duration: value.duration, running: value.running ?? false };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        // Back to monday's own spelling, including the **string** boolean `[cvs§4.4]`.
        const out = { duration: value.duration };
        if (value.running !== undefined)
            out.running = value.running ? 'true' : 'false';
        if (value.startDate !== undefined)
            out.startDate = value.startDate;
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return timeTrackingSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * `history` is **not** in the value: the read path supplies it out of
     * `time_tracking_sessions` and merges it into this fragment (`/v2` resolver, M2 tier-2 §7).
     */
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value) {
        if (value === null)
            return { duration: 0, running: false, started_at: null, updated_at: null };
        return {
            duration: value.duration,
            running: value.running ?? false,
            // Same `+00:00` shape as every other fragment stamp (measured).
            started_at: value.startDate === undefined || value.startDate === null
                ? null
                : toFragmentTimestamp(new Date(value.startDate * 1000)),
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql(ctx) {
        return defaultFilterSql('number', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map